<template>
  <v-dialog
    v-model="dialog"
    max-width="900px"
    scrollable
  >
    <template v-slot:activator="{ on, attrs }">
      <slot name="activator" :on="on" :attrs="attrs"></slot>
    </template>

    <v-card>
      <v-card-title>
        <span class="headline">Документы</span>
        <v-spacer></v-spacer>
        <v-btn
          icon
          small
          :loading="loading"
          @click="loadFiles"
        >
          <v-icon>refresh</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-alert
          v-if="error"
          type="error"
          dismissible
          class="mb-4"
          @input="error = null"
        >
          {{ error }}
        </v-alert>

        <v-row v-if="canUploadLocalFiles" class="mb-2">
          <v-col cols="12" md="8">
            <v-file-input
              v-model="selectedFiles"
              label="Выберите файлы"
              multiple
              show-size
              truncate-length="30"
              prepend-icon="attach_file"
              :disabled="uploading"
              clearable
            ></v-file-input>
          </v-col>
          <v-col cols="12" md="4" class="d-flex align-center">
            <v-btn
              color="primary"
              :loading="uploading"
              :disabled="!hasSelectedFiles"
              @click="uploadFiles"
            >
              <v-icon left>cloud_upload</v-icon>
              Загрузить
            </v-btn>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-card outlined>
              <v-card-title>
                <span>Список документов</span>
              </v-card-title>
              <v-card-text style="max-height: 400px; overflow-y: auto;">
                <div v-if="loading" class="text-center pa-4">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                  ></v-progress-circular>
                  <div class="mt-2">Загрузка документов...</div>
                </div>
                <div v-else-if="filesList.length === 0" class="text-center pa-4 text--secondary">
                  Нет документов
                </div>
                <v-list v-else dense>
                  <v-list-item
                    v-for="file in filesList"
                    :key="fileKey(file)"
                    style="cursor: pointer;"
                    @click="downloadFile(file)"
                  >
                    <v-list-item-icon>
                      <v-icon>insert_drive_file</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ file.filename || file.name }}</v-list-item-title>
                      <v-list-item-subtitle>
                        <span v-if="getFileType(file)">{{ getFileType(file) }}</span>
                        <span v-if="getFileType(file) && file.updated"> • </span>
                        <span v-if="file.updated">{{ file.updated }}</span>
                        <span v-if="file.updated && file.isn != null"> • </span>
                        <span v-if="file.isn != null">ISN: {{ file.isn }}</span>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn
                        icon
                        small
                        :disabled="file.isn == null"
                        :loading="isDownloading(file)"
                        @click.stop="downloadFile(file)"
                      >
                        <v-icon>download</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="dialog = false">
          Закрыть
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import fetch from "@/utils/fetch";
import { getKnownFileExtension } from "@/utils/fileExtension";

export default {
  name: "DocumentsDialog",
  props: {
    contractId: {
      type: Number,
      default: null
    },
    contractNumber: {
      type: [String, Number],
      default: null
    },
    contractIsn: {
      type: [Number, String],
      default: null
    },
    isKiasContract: {
      type: Boolean,
      default: null
    },
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialog: false,
      filesList: [],
      loading: false,
      uploading: false,
      error: null,
      downloadingKey: null,
      selectedFiles: []
    };
  },
  computed: {
    /**
     * Upload только для договоров без ISN.
     * Пока isn/isKias неизвестны — кнопку не показываем.
     */
    canUploadLocalFiles() {
      if (!this.contractId) {
        return false;
      }
      if (this.isKiasContract === true) {
        return false;
      }
      if (this.contractIsn != null && String(this.contractIsn).trim() !== "") {
        return false;
      }
      if (this.isKiasContract === false) {
        return true;
      }
      return false;
    },
    hasSelectedFiles() {
      if (Array.isArray(this.selectedFiles)) {
        return this.selectedFiles.length > 0;
      }
      return !!this.selectedFiles;
    }
  },
  watch: {
    value(val) {
      this.dialog = val;
      if (val) {
        this.loadFiles();
      }
    },
    dialog(val) {
      this.$emit("input", val);
      if (val) {
        this.loadFiles();
      } else {
        this.error = null;
        this.selectedFiles = [];
      }
    }
  },
  methods: {
    fileKey(file) {
      if (file.isn != null) return `centras-${file.isn}`;
      return file.filename || file.name || "file";
    },
    isDownloading(file) {
      return this.downloadingKey === this.fileKey(file);
    },
    async resolveContractNumber() {
      if (
        this.contractNumber != null &&
        String(this.contractNumber).trim() !== ""
      ) {
        return String(this.contractNumber).trim();
      }
      if (!this.contractId) return null;
      const contract = await fetch.get(
        `/api/insurance/contracts/${this.contractId}`
      );
      if (contract && contract.number != null && contract.number !== "") {
        return String(contract.number).trim();
      }
      return null;
    },
    async loadFiles() {
      this.loading = true;
      this.error = null;
      try {
        const contractNumber = await this.resolveContractNumber();
        if (!contractNumber) {
          this.filesList = [];
          return;
        }
        const q = encodeURIComponent(contractNumber);
        const data = await fetch.get(
          `/api/insurance/contracts/centras-files?number=${q}`
        );
        this.filesList = Array.isArray(data) ? data : [];
      } catch (err) {
        this.error = "Ошибка при загрузке документов: " + (err.message || "Неизвестная ошибка");
        console.error("Error loading files:", err);
      } finally {
        this.loading = false;
      }
    },
    async uploadFiles() {
      if (!this.canUploadLocalFiles || !this.hasSelectedFiles) {
        return;
      }
      this.uploading = true;
      this.error = null;
      try {
        const files = Array.isArray(this.selectedFiles)
          ? this.selectedFiles
          : [this.selectedFiles];
        await fetch.uploadCentrasContractFiles({
          files,
          number: this.contractNumber,
          contractId: this.contractId
        });
        this.selectedFiles = [];
        await this.loadFiles();
      } catch (err) {
        this.error = "Ошибка при загрузке файлов: " + (err.message || "Неизвестная ошибка");
        console.error("Error uploading files:", err);
      } finally {
        this.uploading = false;
      }
    },
    getFileType(file) {
      const name = file.filename || file.name;
      if (!name) return null;
      const known = getKnownFileExtension(name);
      if (known) return known;
      const extension = String(name).split(".").pop();
      return extension ? extension.toUpperCase() : null;
    },
    async downloadFile(file) {
      const fallbackName = file.filename || file.name || "file";

      this.downloadingKey = this.fileKey(file);
      this.error = null;

      try {
        if (file.isn == null) {
          throw new Error("Не указан идентификатор файла");
        }
        await fetch.downloadCentrasFile(file.isn, fallbackName);
      } catch (err) {
        this.error = "Ошибка при скачивании файла: " + (err.message || "Неизвестная ошибка");
        console.error("Error downloading file:", err);
      } finally {
        this.downloadingKey = null;
      }
    }
  }
};
</script>
